@file:OptIn(ExperimentalMaterial3Api::class)

package us.laantech.admin.menu.clients

import androidx.compose.foundation.gestures.rememberScrollableState
import androidx.compose.foundation.gestures.scrollable
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.BasicAlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.Card
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.lifecycle.ViewModel
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.lifecycle.viewModelScope
import androidx.lifecycle.viewmodel.compose.viewModel
import androidx.lifecycle.viewmodel.initializer
import androidx.lifecycle.viewmodel.viewModelFactory
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.parameter
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.HttpStatusCode
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import us.laantech.admin.ui.SerializableForm
import us.laantech.admin.util.serializableToMap
import us.laantech.common.dto.company.CompanyBranch
import us.laantech.common.dto.company.ParentCompany
import us.laantech.common.ui.DataTableView
import us.laantech.common.ui.DropdownMenu
import us.laantech.common.util.generateUUIDv7
import us.laantech.common.api.KtorClient

data class CompanyBranchListUiState(
    val parentCompanies: List<ParentCompany> = emptyList(),
    val selectedParentCompany: ParentCompany? = null,
    val branches: List<CompanyBranch> = emptyList(),
    val showCreateBranchModal: Boolean = false,
    val editBranch: CompanyBranch? = null
)

val LocalCompanyBranchListViewModel = compositionLocalOf<CompanyBranchListViewModel> {
    error("No CompanyBranchListViewModel provided")
}

@Composable
fun CompanyBranchListView(
    viewModel: CompanyBranchListViewModel = viewModel(
        factory = viewModelFactory {
            initializer { CompanyBranchListViewModel() }
        }
    )
) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()

    LaunchedEffect(Unit) { viewModel.loadCompanies() }

    CompositionLocalProvider(LocalCompanyBranchListViewModel provides viewModel) {

        Column {
            Row {
                DropdownMenu(
                    label = "Parent Company",
                    items = uiState.parentCompanies,
                    selectedItem = uiState.selectedParentCompany,
                    itemLabel = { it.companyInfo?.companyName ?: "UNKNOWN" },
                    onItemSelected = { viewModel.selectCompany(it) },
                    modifier = Modifier.weight(1f)
                )
                Button(
                    { viewModel.toggleCreateEditBranchModal() },
                    modifier = Modifier.weight(1f)
                ) {
                    Text("Add Branch")
                }
                Button({ viewModel.refreshBranches() }, modifier = Modifier.weight(1f)) {
                    Text("Refresh")
                }

                Text("Card Rate: ${uiState.selectedParentCompany?.cardTransactionRate5dp}")
            }
            DataTableView(
                modifier = Modifier,
                rows = uiState.branches,
            ) {
                column("Store Name", 2f) { Text(it.branchName ?: "NULL", Modifier.weight(2f)) }
                column("Address", 2f) { Text(it.address?.streetAddr1 ?: "NULL", Modifier.weight(2f)) }

                column("CardRate", 1f) {
                    // branch or company card rate
                    val cardRate = it.cardTransactionRate5dp ?: uiState.selectedParentCompany?.cardTransactionRate5dp
                    Text("$cardRate" ?: "NULL", Modifier.weight(1f))
                }

                column("Action", 1f) {
                    Button({
                        viewModel.selectBranchForEdit(it)
                        viewModel.toggleCreateEditBranchModal()
                    }, modifier = Modifier.weight(1f)) {
                        Text("Edit")
                    }
                }
            }
        }
        if (uiState.showCreateBranchModal) {
            CreateEditBranchModal()
        }
    }
}



private val BranchFormJson = Json { ignoreUnknownKeys = true; explicitNulls = false; coerceInputValues = true }

@Composable
fun CreateEditBranchModal() {

    val viewModel = LocalCompanyBranchListViewModel.current
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()

    val parentUuid = uiState.selectedParentCompany?.uuid
        ?: throw IllegalStateException("No parent company selected")

    // Form state is a JSON map (what SerializableForm edits), seeded from a CompanyBranch.
    val branchValues = remember {
        mutableStateOf<Map<String, JsonElement>>(
            serializableToMap(
                CompanyBranch.serializer(),
                uiState.editBranch ?:CompanyBranch(uuid = generateUUIDv7(), parentCompanyUuid = parentUuid),
            )
        )
    }

    val ignoreKeys = setOf(CompanyBranch::departments.name, CompanyBranch::posAds.name)

    BasicAlertDialog(
        onDismissRequest = { viewModel.toggleCreateEditBranchModal() },
    ) {
        Card(Modifier.verticalScroll(rememberScrollState())) {
            Text("Create Branch")
            SerializableForm(
                descriptor = CompanyBranch.serializer().descriptor,
                values = branchValues.value,
                onChange = { branchValues.value = it },
                ignoreKeys = ignoreKeys
            )

            Button({
                val branch = BranchFormJson.decodeFromJsonElement(
                    CompanyBranch.serializer(),
                    JsonObject(branchValues.value),
                )
                viewModel.createBranch(branch)
            }, modifier = Modifier) {
                Text("Create")
            }
        }
    }
}



class CompanyBranchListViewModel(): ViewModel() {

    private val _uiState = MutableStateFlow(CompanyBranchListUiState())
    val uiState = _uiState.asStateFlow()
    val ktClient = KtorClient.instance.client

    fun loadCompanies() {
        viewModelScope.launch {
            val response = ktClient.get("/admin/internal-clients/list")
            val parentCompanies = response.body<List<ParentCompany>>()
            _uiState.update { it.copy(parentCompanies = parentCompanies) }
        }
    }

    fun selectCompany(company: ParentCompany?) {
        _uiState.update { it.copy(selectedParentCompany = company) }
        refreshBranches()
    }

    fun selectBranchForEdit(branch: CompanyBranch?) {
        _uiState.update { it.copy(editBranch = branch) }
    }

    fun createBranch(branch: CompanyBranch) {
        viewModelScope.launch {
            val response = ktClient.post("/admin/internal-clients/branch"){
                setBody(branch)
            }
            if (response.status == HttpStatusCode.Created) {
                refreshBranches()
                selectBranchForEdit(null)
                toggleCreateEditBranchModal()
            }
        }
    }

    fun refreshBranches() {
        viewModelScope.launch {
            val response = ktClient.get("/admin/internal-clients/list/branches") {
                parameter("parentCompanyUuid", _uiState.value.selectedParentCompany?.uuid)
            }
            val branches = response.body<List<CompanyBranch>>()
            _uiState.update { it.copy(branches = branches) }
        }
    }

    fun toggleCreateEditBranchModal() {
        _uiState.update { it.copy(showCreateBranchModal = !_uiState.value.showCreateBranchModal) }
    }

}

